import { useState, useRef, useEffect, useMemo } from "react";
import { ICON_NAMES } from "@adlywise/shared";
import * as LucideIcons from "lucide-react";

const MAX_VISIBLE = 20;

export default function IconPicker({
  value,
  onChange,
  label = "Icon",
  placeholder = "Search lucide icons…",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);

  const displayValue = open ? query : value || "";

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    if (!q) return ICON_NAMES.slice(0, MAX_VISIBLE);
    return ICON_NAMES.filter((n) => n.toLowerCase().includes(q));
  }, [query]);

  const visible = filtered.slice(0, MAX_VISIBLE);
  const overflow = filtered.length - MAX_VISIBLE;

  // Close on outside click
  useEffect(() => {
    const handleMouseDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, []);

  const handleInputFocus = () => {
    setOpen(true);
    setQuery("");
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    setOpen(true);
  };

  const handleSelect = (name) => {
    onChange(name);
    setOpen(false);
    setQuery("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange("");
    setOpen(false);
    setQuery("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
    } else if (e.key === "Enter" && open && visible.length > 0) {
      e.preventDefault();
      handleSelect(visible[0]);
    }
  };

  const SelectedIcon = value ? LucideIcons[value] : null;

  return (
    <div className="relative" ref={containerRef}>
      {label && (
        <label className="mb-1 block text-sm font-medium text-(--text-primary)">
          {label}
        </label>
      )}
      <div className="relative flex items-center">
        {SelectedIcon && !open && (
          <span className="pointer-events-none absolute left-3 flex items-center text-(--text-secondary)">
            <SelectedIcon size={16} strokeWidth={2} />
          </span>
        )}
        <input
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={[
            "w-full rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-1)",
            "py-2 pr-8 text-sm text-(--text-primary) outline-none",
            "focus:border-(--brand-primary)",
            SelectedIcon && !open ? "pl-9" : "pl-3",
          ].join(" ")}
          autoComplete="off"
        />
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 flex items-center justify-center text-(--text-secondary) hover:text-(--text-primary)"
            aria-label="Clear icon"
          >
            <LucideIcons.X size={14} strokeWidth={2} />
          </button>
        )}
      </div>

      {open && (
        <div
          className="absolute left-0 right-0 z-20 mt-1 max-h-56 overflow-y-auto rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-1) shadow-lg"
          role="listbox"
        >
          {visible.length === 0 ? (
            <div className="px-3 py-2 text-sm text-(--text-secondary)">
              No icons found
            </div>
          ) : (
            <>
              {visible.map((name) => {
                const Icon = LucideIcons[name];
                return (
                  <button
                    key={name}
                    type="button"
                    role="option"
                    aria-selected={name === value}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(name);
                    }}
                    className={[
                      "flex w-full items-center gap-2 px-3 py-2 text-sm text-(--text-primary)",
                      "hover:bg-(--surface-2)",
                      name === value ? "bg-(--surface-2) font-medium" : "",
                    ].join(" ")}
                  >
                    {Icon ? (
                      <Icon size={16} strokeWidth={2} />
                    ) : (
                      <span className="h-4 w-4" />
                    )}
                    {name}
                  </button>
                );
              })}
              {overflow > 0 && (
                <div className="px-3 py-2 text-xs text-(--text-secondary)">
                  … and {overflow} more
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
